<script setup lang="ts">
import IconButton from "@/components/IconButton.vue";
import {
  useListActiveTeasers,
  type Teaser,
} from "@/services/orval/internalApiV1";

const CLOSED_VALUE = "closed";
const TEASER_NAME = "marketing_teaser";

const props = defineProps<{
  area: string;
}>();

const { data: teasers } = useListActiveTeasers();

const { userPreference, updateUserPreference } = useUserPreferences();

const activeTeasersForArea = computed<Teaser[]>(() => {
  if (!teasers.value) return [];

  return teasers.value?.collection.filter((teaser) =>
    teaser.areas?.includes(props.area)
  );
});

const firstTeaserForArea = computed(() => {
  if (!activeTeasersForArea.value || activeTeasersForArea.value.length == 0)
    return null;

  const activeTeaser = activeTeasersForArea.value[0];
  const teaserKey = `${TEASER_NAME}_${activeTeaser.id}`;

  if (userPreference(teaserKey).value === CLOSED_VALUE) {
    return null;
  }
  return activeTeasersForArea.value[0];
});

const closeTeaser = async () => {
  if (!firstTeaserForArea.value) return;

  const teaserKey = `${TEASER_NAME}_${firstTeaserForArea.value.id}`;
  await updateUserPreference(teaserKey, CLOSED_VALUE);
};

const isImageTeaser = computed(() => {
  if (!firstTeaserForArea.value) return false;

  return !!firstTeaserForArea.value.image;
});

const teaserUrl = computed(() => {
  if (!firstTeaserForArea.value?.url) return false;
  return firstTeaserForArea.value.url;
});

const component = computed(() => {
  if (isImageTeaser.value && teaserUrl.value) {
    return "a";
  } else {
    return "div";
  }
});

const href = computed(() => {
  if (isImageTeaser.value && teaserUrl.value) {
    return { href: teaserUrl.value };
  }
  return false;
});
</script>

<template>
  <div v-if="firstTeaserForArea" class="teaser" data-testid="teaser-marketing">
    <IconButton
      icon-name="x-lg"
      icon-color="dark"
      type="button"
      size="sm"
      class="close-icon"
      no-padding
      aria-label="Close marketing teaser"
      @click="closeTeaser"
    />
    <component :is="component" v-bind="{ ...href }" target="_blank">
      <template
        v-if="firstTeaserForArea.mobileImage && firstTeaserForArea.image"
      >
        <img
          :src="firstTeaserForArea.mobileImage"
          :alt="firstTeaserForArea.title"
          class="shadow-sm rounded-1 responsive-image d-md-none"
        />
        <img
          class="shadow-sm rounded-1 responsive-image d-none d-md-block"
          :src="firstTeaserForArea.image"
          :alt="firstTeaserForArea.title"
        />
      </template>
      <template v-else-if="firstTeaserForArea.image">
        <img
          class="shadow-sm rounded-1 responsive-image"
          :src="firstTeaserForArea.image"
          :alt="firstTeaserForArea.title"
        />
      </template>
    </component>
  </div>
</template>

<style lang="scss" scoped>
.teaser {
  position: relative;
  margin: 0 auto;
  transition: transform 0.1s ease-out;

  &:hover {
    transform: scale(1.03);
  }
}

.close-icon {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 1;

  &:hover {
    background-color: $color-surface-white;
  }
}
</style>
